import { useCallback } from "react";
import {
  Pressable,
  type StyleProp,
  StyleSheet,
  View,
  type ViewStyle,
  useWindowDimensions,
} from "react-native";
import { default as RNModal } from "react-native-modal";

import { isNative, useDebouncedCallback } from "@stepapp/core";

import { useBreakpoints } from "../hooks/useBreakpoints";
import { palette, spacing } from "../styles";
import Block from "./Block";
import Button from "./Button";
import Typography from "./Typography";

type Props = {
  avoidKeyboard?: boolean;
  children?: NotWorth;
  contentStyle?: StyleProp<ViewStyle>;
  enableScroll?: boolean;
  handleOnPress?: () => void;
  primaryAction?: () => void;
  secondaryAction?: () => void;
  propagateSwipe?: boolean;
  toggle: () => void;
  visible: boolean;
  title?: string;
  description?: string;
  secondaryActionLabel?: string;
  primaryActionLabel: string;
  loading?: boolean;
};

const Modal = ({
  propagateSwipe,
  avoidKeyboard = false,
  visible,
  children,
  toggle,
  contentStyle,
  enableScroll = isNative,
  handleOnPress,
  primaryAction,
  secondaryAction,
  title,
  description,
  secondaryActionLabel,
  primaryActionLabel,
  loading = false,
}: Props) => {
  const { md } = useBreakpoints();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const width = md ? 580 - 40 : screenWidth - 40;

  const debounceToggle = useDebouncedCallback(toggle, 300);

  const Wrapper = handleOnPress ? Pressable : View;

  const handleSecondaryAction = useCallback(() => {
    if (secondaryAction) {
      return secondaryAction();
    }
    toggle();
  }, [secondaryAction, toggle]);

  return (
    <RNModal
      hideModalContentWhileAnimating
      useNativeDriverForBackdrop
      animationOutTiming={500}
      avoidKeyboard={avoidKeyboard}
      customBackdrop={
        <Pressable
          style={[styles.backdrop, { width: screenWidth, minHeight: screenHeight }]}
          onPress={() => toggle()}
        />
      }
      backdropOpacity={0.5}
      isVisible={visible}
      onBackButtonPress={debounceToggle}
      onBackdropPress={debounceToggle}
      onSwipeComplete={debounceToggle}
      propagateSwipe={propagateSwipe}
      style={styles.container}
      useNativeDriver={false}
      {...(enableScroll && { swipeDirection: "down" })}
    >
      <Wrapper onPress={handleOnPress}>
        <Block style={[styles.content, { width }, contentStyle]}>
          <Block>
            {title && (
              <Typography style={styles.title} variant="sectionTitle">
                {title}
              </Typography>
            )}
            {description && (
              <Typography style={styles.description} type="secondary" variant="body">
                {description}
              </Typography>
            )}

            {primaryAction && (
              <Block style={styles.buttonsContainer}>
                <Button
                  disabled={loading}
                  label={primaryActionLabel}
                  loading={loading}
                  onPress={primaryAction}
                  type="primary"
                />
                {!!secondaryActionLabel && (
                  <Block marginTop={2}>
                    <Button
                      disabled={loading}
                      label={secondaryActionLabel || ""}
                      onPress={handleSecondaryAction}
                      type="secondary"
                    />
                  </Block>
                )}
              </Block>
            )}
          </Block>
          {children}
        </Block>
      </Wrapper>
    </RNModal>
  );
};

export default Modal;

const styles = StyleSheet.create({
  buttonsContainer: {
    marginTop: spacing.unit * 2.5,
  },
  backdrop: {
    flex: 1,
    backgroundColor: palette.common.black,
  },
  container: {
    alignItems: "center",
    justifyContent: "center",
    margin: 0,
  },
  content: {
    backgroundColor: palette.common.white,
    borderRadius: spacing.radius * 2,
    paddingHorizontal: spacing.unit * 3.5,
    paddingVertical: spacing.unit * 3,
  },
  description: {
    marginBottom: spacing.unit * 2.5,
    textAlign: "center",
  },
  title: {
    marginBottom: spacing.unit * 3,
    textAlign: "center",
  },
});
